import type { EmployeeWithSchedule, HolidayDTO, PlannedDayEntry, PlannedEmployeeWeek } from '@functieloonhuis/shared';
import { getWeekType, getWorkweekDates, getMondayOfWeek, getWorkdaysOfMonth, toDateString } from './dateUtils';
import { getDayEntryForDay } from './weekUtils';

/**
 * Map schedule patterns (Week A/B) to concrete dates for a given week.
 *
 * For each employee:
 * 1. Determine if the target week is A or B
 * 2. Pick the corresponding weekA or weekB entries
 * 3. Map each dayOfWeek to the real date
 * 4. Overlay holidays (holiday overrides work schedule)
 */
export function mapScheduleToWeek(
  employees: EmployeeWithSchedule[],
  weekStart: Date,
  holidays: HolidayDTO[],
): PlannedEmployeeWeek[] {
  const weekType = getWeekType(weekStart);
  const dates = getWorkweekDates(weekStart);

  // Build holiday lookup: date string → holiday name
  const holidayMap = new Map<string, string>();
  for (const h of holidays) {
    holidayMap.set(h.date, h.name);
  }

  return employees.map(emp => {
    const entries = weekType === 'A' ? emp.weekA : emp.weekB;

    const days: PlannedDayEntry[] = dates.map((date, i) => {
      const dayOfWeek = i + 1; // 1=Mon, 7=Sun
      const dateStr = toDateString(date);
      const holidayName = holidayMap.get(dateStr);
      const entry = getDayEntryForDay(entries, dayOfWeek);

      if (holidayName) {
        return {
          date: dateStr,
          dayOfWeek,
          weekType,
          isWorkday: false,
          location: null,
          isHalfDay: false,
          halfDayPeriod: null,
          startTime: null,
          endTime: null,
          isHoliday: true,
          holidayName,
        };
      }

      return {
        date: dateStr,
        dayOfWeek,
        weekType,
        isWorkday: entry?.isWorkday ?? false,
        location: entry?.location ?? null,
        isHalfDay: entry?.isHalfDay ?? false,
        halfDayPeriod: entry?.halfDayPeriod ?? null,
        startTime: entry?.startTime ?? null,
        endTime: entry?.endTime ?? null,
        isHoliday: false,
        holidayName: null,
      };
    });

    return {
      employee: emp.employee,
      days,
    };
  });
}

/**
 * Map schedule patterns to all workday (Mon–Fri) dates in a given month.
 *
 * Returns one PlannedEmployeeWeek per employee where `days` contains
 * every Mon–Fri of the month (typically 20–23 days).
 */
export function mapScheduleToMonth(
  employees: EmployeeWithSchedule[],
  year: number,
  month: number, // 0-indexed
  holidays: HolidayDTO[],
): PlannedEmployeeWeek[] {
  const workdays = getWorkdaysOfMonth(year, month);

  // Build holiday lookup
  const holidayMap = new Map<string, string>();
  for (const h of holidays) {
    holidayMap.set(h.date, h.name);
  }

  return employees.map(emp => {
    const days: PlannedDayEntry[] = workdays.map(date => {
      const weekType = getWeekType(getMondayOfWeek(date));
      const entries = weekType === 'A' ? emp.weekA : emp.weekB;
      const dayOfWeek = date.getDay() === 0 ? 7 : date.getDay(); // ISO: 1=Mon
      const dateStr = toDateString(date);
      const holidayName = holidayMap.get(dateStr);
      const entry = getDayEntryForDay(entries, dayOfWeek);

      if (holidayName) {
        return {
          date: dateStr,
          dayOfWeek,
          weekType,
          isWorkday: false,
          location: null,
          isHalfDay: false,
          halfDayPeriod: null,
          startTime: null,
          endTime: null,
          isHoliday: true,
          holidayName,
        };
      }

      return {
        date: dateStr,
        dayOfWeek,
        weekType,
        isWorkday: entry?.isWorkday ?? false,
        location: entry?.location ?? null,
        isHalfDay: entry?.isHalfDay ?? false,
        halfDayPeriod: entry?.halfDayPeriod ?? null,
        startTime: entry?.startTime ?? null,
        endTime: entry?.endTime ?? null,
        isHoliday: false,
        holidayName: null,
      };
    });

    return { employee: emp.employee, days };
  });
}
